import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

const initialState = {
  isloading: false,
  products: [],
  isError: false,
  sortType: "default",
  singleProduct: null,
  activeFilters: {
    categories: [],
    locations: [],
    priceRange: [0, 2500],
  },
  filteredProducts: [],
};

const token = localStorage.getItem("token");
const validateToken = token && token.length > 0;

const productSlice = createSlice({
  name: "product",
  initialState: {
    ...initialState,
    bestSellerProducts: [],
    arrivalProducts: [],
    popularProducts: [],
    specialityProducts: [],
    searchResults: [],
  },
  reducers: {
    setIsLoading: (state) => {
      state.isloading = true;
    },
    setApiData: (state, action) => {
      const bestSellerData = action.payload.filter((product) => {
        return product.productType === "Best Seller";
      });

      const arrivalData = action.payload.filter((product) => {
        return product.productType === "New Arrival";
      });
      const popularData = action.payload.filter((product) => {
        return product.productType === "Popular Products";
      });
      const allProducts = action.payload;

      // // Helper function to check if a product matches a type
      // const filterByType = (products, type) => {
      //   return products.filter(
      //     (product) => product.productType && product.productType === type
      //   );
      // };

      // // Filter products by type
      // const bestSellerData = filterByType(allProducts, "Bestsellers");
      // console.log("bestSellerData in productSlice", bestSellerData);
      // const arrivalData = filterByType(allProducts, "New Arrival");
      // console.log("arrivalData in productSlice", arrivalData);
      // const popularData = filterByType(allProducts, "Popular Products");
      // console.log("popularData in productSlice", popularData);
      state.products = allProducts;
      state.bestSellerProducts = bestSellerData;
      state.arrivalProducts = arrivalData;
      state.popularProducts = popularData;
      state.isloading = false;
      state.isError = null;
    },
    setFilteredProducts: (state, action) => {
      const { specialityProducts, allProducts } = action.payload;
      state.specialityProducts = specialityProducts || [];
      state.filteredProducts = allProducts || [];
      state.isloading = false;
      state.isError = null;
    },
    setError: (state, action) => {
      state.isError = action.payload;
      state.isloading = false;
    },
    setSortType: (state, action) => {
      // console.log("sort", action.payload)
      state.sortType = action.payload;
      state.isloading = false;
    },
    setSingleApiData: (state, action) => {
      state.singleProduct = action.payload;
      state.isloading = false;
      state.isError = null;
    },
    setSearchResults: (state, action) => {
      // console.log("ss",action.payload)
      state.searchResults = action.payload;
      state.isloading = false;
      state.isError = null;
    },
    setFilters: (state, action) => {
      state.activeFilters = {
        ...state.activeFilters,
        ...action.payload,
      };

      let filtered = [...state.products];

      // Filter by categories
      if (state.activeFilters.categories.length > 0) {
        filtered = filtered.filter((product) =>
          state.activeFilters.categories.includes(product.category._id)
        );
      }

      // Filter by locations - Handle both location formats
      if (state.activeFilters.locations.length > 0) {
        filtered = filtered.filter((product) =>
          state.activeFilters.locations.some((loc) =>
            product.location.includes(loc)
          )
        );
      }

      // Filter by price range
      const [minPrice, maxPrice] = state.activeFilters.priceRange;
      filtered = filtered.filter((product) => {
        const weightObj = product.weights[0];
        if (!weightObj) return false;

        const basePrice = weightObj.prices.India;
        const discountedPrice =
          basePrice - (basePrice * product.discount_percentage) / 100;
        return discountedPrice >= minPrice && discountedPrice <= maxPrice;
      });

      state.noProductsFound = filtered.length === 0;
      state.filteredProducts = filtered;
    },
    clearFilters: (state) => {
      state.activeFilters = {
        categories: [],
        locations: [],
        priceRange: [0, 2500],
      };
      state.filteredProducts = [];
    },
  },
});

// Export the actions generated by createSlice
export const {
  setIsLoading,
  setApiData,
  setError,
  setSortType,
  setSingleApiData,
  setFilteredProducts,
  setSearchResults,
  setCurrentState,
  setFilters,
  clearFilters,
  updateStock,
} = productSlice.actions;

// API call constants
const API_URLS = {
  products: `${process.env.REACT_APP_BACKEND_URL}/api/products/available`,
  singleProduct: (slug) => `${process.env.REACT_APP_BACKEND_URL}/api/products/slug/${slug}`,
  userFilter: `${process.env.REACT_APP_BACKEND_URL}/api/products/user/filter`,
  userSearch: `${process.env.REACT_APP_BACKEND_URL}/api/products/user/productSearch`,
};
//  fetching all products
export const fetchProducts = (location) => async (dispatch) => {
  dispatch(setIsLoading());
  try {
    const url = location
      ? `${API_URLS.products}?location=${location}`
      : `${API_URLS.products}`;

    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${validateToken}`,
      },
    });
    const data = await response.json();
    const { success, message, responseObject } = data;
    if (success) {
      dispatch(setApiData(responseObject));
    } else {
      dispatch(setError(message));
      toast.error(message);
    }
  } catch (error) {
    // Handle 500 error specifically
    if (error.response?.status === 500) {
      const errorMessage = "Server error occurred. Please try again later.";
      dispatch(setError(errorMessage));
      toast.error(errorMessage);

      // Log the error details for debugging
      // console.log("Server Error Details:", {
      //   status: error.response.status,
      //   data: error.response.data,
      //   headers: error.response.headers,
      // });
    } else {
      dispatch(setError(error.message));
      toast.error(error.message);
    }
    dispatch(
      setError(error.message || "An error occurred while fetching products.")
    );
    toast.error("Failed to load products.");
    console.error(error);
  }
};

// fetching single product
export const fetchSingleProducts = (slug) => async (dispatch) => {
  if (!slug) {
    console.error("No slug provided to fetchSingleProducts");
    dispatch(setError("Product identifier is required"));
    toast.error("Product identifier is required");
    return;
  }

  console.log("Making API request for slug:", slug); // Debug log

  try {
    const response = await fetch(API_URLS.singleProduct(slug), {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log("API Response:", data); // Debug log

    const { success, message, responseObject } = data;
    if (success) {
      dispatch(setSingleApiData(responseObject));
    } else {
      dispatch(setError(message));
      toast.error(message);
    }
  } catch (error) {
    console.error("Error in fetchSingleProducts:", error);
    dispatch(setError(error.message || "Failed to fetch product details."));
    toast.error("Failed to fetch product details.");
  }
};

// filter product api
export const filterProductsApiOldVersion = (filters) => async (dispatch) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.category) queryParams.append("category", filters.category);
    if (filters.price) queryParams.append("price", filters.price);
    if (filters.state) queryParams.append("location", filters.state);
    if (filters.sort) queryParams.append("sort", filters.sort);

    // First, fetch the state details with specialty products
    const stateResponse = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/states/active?withSpeciality=true`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    const stateData = await stateResponse.json();

    // Get specialty products for the selected state
    const selectedState = filters.state
      ? stateData.responseObject.find(
          (state) => state.stateName === filters.state
        )
      : null;

    // Create a Set of specialty product IDs for faster lookup
    const specialtyProductIds = new Set(
      selectedState?.speciality?.map((product) => product?._id) || []
    );

    // Fetch filtered products
    const productResponse = await fetch(
      `${
        process.env.REACT_APP_BACKEND_URL
      }/api/products/filter?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    const productData = await productResponse.json();

    if (productData.success) {
      const allProducts = productData.responseObject;

      // Separate specialty and regular products
      const specialityProducts = [];
      const regularProducts = [];

      allProducts.forEach((product) => {
        if (specialtyProductIds.has(product?._id)) {
          specialityProducts.push(product);
        } else {
          regularProducts.push(product);
        }
      });

      // console.log("Specialty products count:", specialityProducts.length);
      // console.log("Regular products count:", regularProducts.length);

      dispatch(
        setFilteredProducts({
          specialityProducts,
          allProducts: regularProducts,
        })
      );
    } else {
      dispatch(setError(productData.message));
      toast.error(productData.message);
    }
  } catch (error) {
    console.error("Filter products error:", error);
    dispatch(setError(error.message));
    toast.error(error.message);
  }
};

// export const searchProducts = (searchTerm) => async (dispatch) => {
//   // console.log("searchTerm",searchTerm)
//   dispatch(setIsLoading());
//   try {
//     const res = await fetch(
//       `${process.env.REACT_APP_BACKEND_URL}/api/products/productSearch?name=${searchTerm}`,
//       {
//         method: "GET",
//         headers: {
//           "Content-Type": "application/json",
//         },
//       }
//     );
//     const data = await res.json();
//     const { success, message, responseObject } = data;
//     if (success) {
//       dispatch(setSearchResults(responseObject));
//     } else {
//       dispatch(setError(message));
//       toast.error(message);
//     }
//   } catch (error) {
//     dispatch(setError(error.message));
//     toast.error(error.message || "Search failed");
//     console.log("search product error", error);
//   }
// };

// Add these new exports for user-specific routes
export const filterProductsApi = (filters) => async (dispatch) => {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams();
    // console.log("filters in filterProductsApi", filters);
    // Only append parameters that have values
    if (filters.category) queryParams.append("category", filters.category);
    if (filters.location) queryParams.append("location", filters.location);
    if (filters.price) {
      const [minPrice, maxPrice] = filters.price.split("-");
      queryParams.append("minPrice", minPrice);
      queryParams.append("maxPrice", maxPrice);
    }

    // Make the API call with query parameters
    const response = await fetch(
      `${
        process.env.REACT_APP_BACKEND_URL
      }/api/products/user/filter?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    const data = await response.json();
    // console.log("data in filterProductsApi", data);
    if (data.success) {
      // Update the filtered products in the store
      dispatch(
        setFilteredProducts({
          allProducts: data.responseObject.products || [],
          specialityProducts: data.responseObject.specialityProducts || []
        })
      );
    } else {
      dispatch(setError(data.message));
      toast.error(data.message);
    }
  } catch (error) {
    console.error("Filter error:", error);
    dispatch(setError(error.message));
    toast.error(error.message || "Failed to filter products");
  }
};

export const searchProducts = (searchTerm) => async (dispatch) => {
  dispatch(setIsLoading());
  try {
    const res = await fetch(`${API_URLS.userSearch}?name=${searchTerm}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const data = await res.json();
    const { success, message, responseObject } = data;
    if (success) {
      dispatch(setSearchResults(responseObject));
    } else {
      dispatch(setError(message));
      toast.error(message);
    }
  } catch (error) {
    dispatch(setError(error.message));
    toast.error(error.message || "Search failed");
    // console.log("search product error", error);
  }
};

export default productSlice.reducer;
